import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { QuotationStateEnums } from 'src/app/enum/quotation-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { QuotationVersionModel } from 'src/app/models/preliminaries/quotation-version.model';
import { QuotationModel } from 'src/app/models/preliminaries/quotation.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { QuotationVersionTableModel } from 'src/app/models/tables/preliminaries/quotation-version-table.model';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { QuotationVersionService } from 'src/app/services/preliminaries/quotation-version.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-register-quotation-version',
  templateUrl: './register-quotation-version.component.html',
  styleUrls: ['./register-quotation-version.component.scss'],
  standalone: false
})
export class RegisterQuotationVersionComponent implements OnInit {
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<QuotationVersionTableModel> = new PagedResponseModel<QuotationVersionTableModel>();
  disabledForm: boolean = false;
  formQuotationVersion: FormGroup = new FormGroup({});
  header: ColumnResponseModel[] = [];
  isFormView: boolean = false;
  lastQuotationVersionModel: QuotationVersionModel = new QuotationVersionModel();
  limit: number = 10;
  quotationId: number = 0;
  quotationVersionId: number = 0;
  quotationVersionContract: QuotationVersionModel = new QuotationVersionModel();
  quotationContract: QuotationModel = new QuotationModel();
  page: number = 1;
  permissionUpdatable: boolean = false;
  suscription: Subscription;
  tableActions: TableAction[] = [];

  constructor(private formBuilder: FormBuilder,
    private dateConverterService: DateConverterService,
    public config: DynamicDialogConfig,
    private toastHandlerService: ToastHandlerService,
    @Optional() private ref: DynamicDialogRef,
    private quotationVersionService: QuotationVersionService) {
    this.validateForm(new QuotationVersionModel());
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }

    this.suscription = input$?.subscribe((inputs: any) => {
      this.quotationContract = inputs.quotationModel;
      this.permissionUpdatable = inputs.permissionUpdatable;
    });

    this.quotationId = this.quotationContract.quotationId;
    this.formQuotationVersion.controls["quotationConsecutive"].patchValue(this.quotationContract.consecutive);
    this.disabledForm = this.quotationContract.quotationStateId !== QuotationStateEnums.open;
    this.tableActions = this.disabledForm ? [] : this.getDefaultActions();

    if (this.disabledForm) {
      setTimeout(() => this.formQuotationVersion.disable(), 100);
    }

    await Promise.all([
      this.getColumnsForConsultation(),
      this.getAllQuotationVersionsWithPagination()
    ]);

  }

  ngOnDestroy() {
    this.suscription?.unsubscribe();
  }

  async changeConsecutive() {
    const consecutive = this.formQuotationVersion.get("consecutive")?.value;

    if (!consecutive || consecutive <= 0) {
      this.quotationVersionId = 0;
      this.quotationVersionContract = new QuotationVersionModel();
      return;
    }

    try {
      const contract = await firstValueFrom(this.quotationVersionService.getQuotationVersionByKeys(this.quotationId, consecutive));
      this.quotationVersionContract = contract;
      this.quotationVersionId = contract.quotationVersionId;
      this.validateForm(contract);

      this.toggleFormByState(contract.active);
    } catch (error) {
      console.error("Error en changeConsecutive:", error);
    }
  }

  private toggleFormByState(isActive: boolean) {
    if (isActive) {
      setTimeout(() => this.formQuotationVersion.enable(), 100);
      return;
    }

    setTimeout(() => this.formQuotationVersion.disable(), 100);
    setTimeout(() => this.formQuotationVersion.controls['consecutive'].enable(), 150);
  }

  changeStateVersion() {
    if (this.permissionUpdatable) {
      const message = this.quotationVersionContract.active ? '¿Realmente desea cerrar la versión?' : '¿Realmente desea abrir la versión?';
      Swal.fire({
        title: 'Atención',
        text: message,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Si',
        cancelButtonText: 'No'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await firstValueFrom(this.quotationVersionService.setActive(this.quotationVersionContract.quotationVersionId));
            this.toastHandlerService.showToast('success', 'La versión se modificó exitosamente.', '');
            this.getAllQuotationVersionsWithPagination();
            this.quotationVersionContract = new QuotationVersionModel();
            this.validateForm(this.quotationVersionContract);
            this.quotationVersionId = 0;
            setTimeout(() => this.formQuotationVersion.enable());
            this.emitQuotationVersion(response, true);
          } catch (error) {
            console.error('Error catch:', error);
          }
        }
      });
    } else {
      this.toastHandlerService.showToast('warning', 'El usuario no tiene permisos para modificar el estado de la versión. Comuníquese con el administrador del sistema.', 'Atención');
    }
  }

  clearForm() {
    this.quotationVersionId = 0;
    this.quotationVersionContract = new QuotationVersionModel();
    this.isFormView = false;
  }

  async createNewQuotationVersion() {
    await this.getQuotationVersionsByQuotationId();
    if (this.lastQuotationVersionModel && this.lastQuotationVersionModel.active) {
      this.toastHandlerService.showToast('warning', 'No es posible crear una nueva versión si la última está abierta', '');
    } else {
      this.quotationVersionContract = new QuotationVersionModel();
      this.validateForm(new QuotationVersionModel());
      this.quotationVersionId = 0;
      setTimeout(() => this.formQuotationVersion.controls["consecutive"].disable(), 100);
      this.isFormView = true;
    }
  }

  emitQuotationVersion(quotationVersionModel: any, isLastVersion: boolean) {
    quotationVersionModel.isLastVersion = isLastVersion;
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'select', payload: quotationVersionModel });
  }

  async getAllQuotationVersionsWithPagination() {
    try {
      this.detailData = await firstValueFrom(
        this.quotationVersionService.getAllQuotationVersionsWithPagination(
          this.quotationId, this.page, this.limit, this.columnSort, this.ascendant
        ));
    } catch (error) {
      console.error('Error al buscar las versiones:', error);
    }
  }

  async getColumnsForConsultation() {
    try {
      this.header = await firstValueFrom(this.quotationVersionService.getColumnsForConsultation());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta de versiones.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    return [
      {
        label: 'Seleccionar',
        icon: 'pi pi-check-circle',
        action: 'select'
      },
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        action: 'edit'
      },
      {
        label: 'Eliminar',
        icon: 'pi pi-trash',
        action: 'delete'
      }
    ];
  }

  async getQuotationVersionsByQuotationId() {
    try {
      const response = await firstValueFrom(this.quotationVersionService.getQuotationVersionsByQuotationId(this.quotationId));
      this.lastQuotationVersionModel = response[0]!;
    } catch (error) {
      console.error('Error catch:', error);
    }
  }

  async onAction(event: { action: string, item?: QuotationVersionTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      try {
        this.quotationVersionContract = await firstValueFrom(this.quotationVersionService.getQuotationVersionById(event.item.id));
        this.quotationVersionId = this.quotationVersionContract.quotationVersionId;
        this.validateForm(this.quotationVersionContract);
        this.toggleFormByState(this.quotationVersionContract.active);
        this.isFormView = true;
      } catch (error) {
        console.error('Error to edit repair:', error);
      }
    }
    if (event.action === 'delete' && event.item) {
      const result = await Swal.fire({
        title: 'Atención',
        text: "¿Realmente desea eliminar la versión?",
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Si',
        cancelButtonText: 'No'
      });
      if (result.isConfirmed) {
        try {
          await firstValueFrom(this.quotationVersionService.deleteQuotationVersion(event.item.id));
          this.validateForm(new QuotationVersionModel());
          this.quotationVersionId = 0;

          this.toastHandlerService.showToast(
            'success',
            'La versión fue eliminada exitosamente.'
          );
          this.clearForm();
          this.getAllQuotationVersionsWithPagination();
          const output$ = this.config.data?.output$;
          output$?.next({ type: 'select', payload: new QuotationVersionModel() });
        } catch (error) {
          console.error('Error to delete repair:', error);
        }
      }
    } if (event.action === 'select' && event.item) {
      this.emitQuotationVersion(event.item, false);
      if (this.ref) this.ref.close();
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getAllQuotationVersionsWithPagination();
  }

  async send() {
    Object.keys(this.formQuotationVersion.controls).forEach(key => {
      const control = this.formQuotationVersion.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });

    if (this.formQuotationVersion.valid) {
      this.formQuotationVersion.controls['consecutive'].enable();
      const modelQuotationVersion = this.formQuotationVersion.getRawValue();
      modelQuotationVersion.observation = modelQuotationVersion.observation || null;

      if (this.quotationVersionId == 0) {
        modelQuotationVersion.quotationId = this.quotationId;
        try {
          const response = await firstValueFrom(this.quotationVersionService.createQuotationVersion(modelQuotationVersion));
          this.quotationVersionId = response.quotationId;
          this.quotationVersionContract = response;
          this.emitQuotationVersion(response, true);
          this.toastHandlerService.showToast('success', 'La versión fue creada exitosamente', '');
        } catch (error) {
          console.error('Error al guardar:', error);
        }
      } else {

        this.quotationVersionContract.dateVersion = modelQuotationVersion.dateVersion;
        this.quotationVersionContract.observation = modelQuotationVersion.observation;
        try {
          const response = await firstValueFrom(this.quotationVersionService.updateQuotationVersion(this.quotationVersionContract));
          this.quotationVersionContract = response;
          this.emitQuotationVersion(response, true);

          this.toastHandlerService.showToast('success', 'La versión fue modificada exitosamente', '');
        } catch (error) {
          console.error('Error al editar:', error);
        }
      }

      this.validateForm(new QuotationVersionModel());
      this.getAllQuotationVersionsWithPagination();
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      quotationId: {
        required: 'La cotización es obligatoria.',
      },
      dateVersion: {
        required: 'El campo feha es obligatorio.',
      },
      detailText: {
        maxlength: 'La observación no puede tener más de 250 caracteres.'
      },
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formQuotationVersion.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(quotationVersion: QuotationVersionModel) {
    this.formQuotationVersion = this.formBuilder.group({
      quotationId: [quotationVersion.quotationId, [Validators.required]],
      quotationConsecutive: [],
      consecutive: [quotationVersion.consecutive, [Validators.required]],
      dateVersion: [quotationVersion.dateVersion ?
        this.dateConverterService.convertUtcToLocalDate(quotationVersion.dateVersion) : null, [Validators.required]],
      active: [quotationVersion.active, []],
      observation: [quotationVersion.observation, [Validators.maxLength(250)]]
    });


    this.formQuotationVersion.controls["quotationConsecutive"].patchValue(this.quotationContract.consecutive);
    setTimeout(() => this.formQuotationVersion.controls["quotationConsecutive"].disable(), 100);

  }
}